'use client';

import { type ClassValue } from 'clsx';
import { useEffect, useRef, useState } from 'react';
import Image, { StaticImageData } from 'next/image';
import heroSection from '@/public/lander_hero.jpg';
import heroSectionMob from '@/public/lander_hero_mobile.jpg';
import FadeOut from '@/components/ui/Animations/FadeOut';
import ParallaxImage from '../ParallaxImage/ParallaxImage';

interface HeroCarouselProps {
  className?: ClassValue;
  children?: React.ReactNode;
  desktopImage?: string | StaticImageData;
  desktopDepthImage?: string | StaticImageData;
  mobileImage?: string | StaticImageData;
  height?: number;
  color?: string;
}

export default function HeroImageSection(props: HeroCarouselProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const lowerDecorRef = useRef<HTMLDivElement>(null);
  const [sectionHeightInitialized, setSectionHeightInitialized] =
    useState(false);
  const [hideDarkOverlay, setHideDarkOverlay] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);

    //Sets section height to viewport height + lower decor height. Fixes the issue of the scroll jumping when browser address bar hides on scroll.
    const setHeight = () => {
      const clientHeight = props.height
        ? props.height
        : document.documentElement.clientHeight;
      if (sectionRef.current) {
        const decorHeight = lowerDecorRef?.current?.clientHeight;
        const height = decorHeight ? clientHeight + decorHeight : clientHeight;
        sectionRef.current.style.setProperty(
          'height',
          `${height}px`,
          'important'
        );
        setSectionHeightInitialized(true);
      }
    };

    setHeight();

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <>
      {!hideDarkOverlay && (
        <FadeOut
          animation={{ duration: 1 }}
          className="absolute inset-0 bg-black z-50"
          onAnimationComplete={() => setHideDarkOverlay(true)}
        ></FadeOut>
      )}
      <section
        ref={sectionRef}
        className={`w-full h-[100dvh] relative overflow-hidden ${props.className ? props.className : ''}`}
      >
        {isMobile ? (
          <Image
            src={
              (typeof props.mobileImage === 'string'
                ? props.mobileImage
                : props.mobileImage?.src) || heroSectionMob
            }
            alt={'Hero image'}
            fill
            className="absolute inset-0 w-full h-full object-cover object-center -z-1"
          />
        ) : props.desktopDepthImage ? (
          <>
            <Image
              src={
                (typeof props.desktopImage === 'string'
                  ? props.desktopImage
                  : props.desktopImage?.src) || heroSection
              }
              alt={'Hero image loader'}
              fill
              className="block absolute inset-0 w-[100vw] h-full object-cover object-center -z-1"
            />
            <ParallaxImage
              srcImagePath={
                typeof props.desktopImage === 'string'
                  ? props.desktopImage
                  : props.desktopImage?.src || '/hero-section.jpg'
              }
              depthMapPath={
                typeof props.desktopDepthImage === 'string'
                  ? props.desktopDepthImage
                  : props.desktopDepthImage?.src || '/hero-depth.jpg'
              }
              className="absolute inset-0 w-full h-full object-cover object-bottom -z-1"
            />
          </>
        ) : (
          <Image
            src={
              (typeof props.desktopImage === 'string'
                ? props.desktopImage
                : props.desktopImage?.src) || heroSection
            }
            alt={'Hero image loader'}
            fill
            className="block absolute inset-0 w-[100vw] h-full object-cover object-center -z-1"
          />
        )}
        <div className="relative w-full h-full justify-self-center top-0 left-0 bg-gradient-to-t md:bg-gradient-to-r  from-black/50 from-40% via-transparent to-transparent">
          {props.children}
        </div>
        <div
          ref={lowerDecorRef}
          className={`h-[40px] w-full rounded-t-3xl bg-${props.color || 'white'} absolute bottom-[-1px] left-0`}
        ></div>
      </section>
    </>
  );
}
